import { createShapeId, type TLNoteShape } from "@tldraw/tlschema";
import { startEditingShapeWithRichText } from "./canvas";
import { useValue } from "@tldraw/state-react";
import type { VecLike } from "./canvas";
import { setZoomPercent } from "./ZoomControl";
import { snapEndpoints, setSnapEndpoints } from "./snap-settings";
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Maximize, Palette, Hand } from "lucide-react";
import type { WorkspaceModel } from "./useWorkspace";
import { savedMouseWheelZoom, setMouseWheelZoom } from "./canvas-navigation";
import { notify } from "./api";
import { pasteAtCanvasPoint } from "./canvas-clipboard";
import { imagePlacement, sheetAt } from "./sheet-operations";
import { addTextAt } from "./quick-add";
import { controllers } from "./tools";

import type { CanvasAppearance } from "./useCanvasAppearance";

type ContextPoint = { x: number; y: number; page?: VecLike };
const pasteShortcut = /Mac|iPhone|iPad/.test(navigator.platform)
  ? "⌘V"
  : "Ctrl+V";

export function CanvasViewControls({
  model,
  appearance,
}: {
  model: WorkspaceModel;
  appearance: CanvasAppearance;
}) {
  const { view, setView } = appearance;
  const { editor, active, canComment } = model;
  const [menu, setMenu] = useState<
    "fit" | "background" | "snap" | "navigation" | "canvas" | null
  >(null);
  const [wheelZoom, setWheelZoom] = useState(savedMouseWheelZoom);
  const [point, setPoint] = useState<ContextPoint | null>(null);
  const canEdit = useValue(
    "canvas menu editable",
    () => model.canEdit && !editor.getIsReadonly(),
    [editor, model.canEdit],
  );
  const objectSnap = useValue(
    "object snap",
    () => editor.user.getIsSnapMode(),
    [editor],
  );
  const [endpoints, setEndpoints] = useState(() => snapEndpoints(editor));
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = editor.getContainer();
    const context = (e: MouseEvent) => {
      if (
        !(e.target instanceof Element) ||
        e.defaultPrevented ||
        e.target.closest(
          "button,a,input,textarea,[contenteditable=true],[role=dialog]",
        )
      )
        return;
      const page = editor.screenToPage({ x: e.clientX, y: e.clientY });
      const hit = editor.getShapeAtPoint(page, {
        hitInside: true,
        hitLocked: true,
      });
      // Empty canvas and bare sheet paper share the canvas actions; objects
      // keep their own menus (PDF, file content) or none.
      if (hit && hit.type !== "sheet") return;
      e.preventDefault();
      e.stopPropagation();
      setPoint({
        x: Math.max(8, Math.min(window.innerWidth - 256, e.clientX)),
        y: Math.max(8, Math.min(window.innerHeight - 400, e.clientY)),
        page,
      });
      setMenu("canvas");
    };
    container.addEventListener("contextmenu", context, true);
    return () => container.removeEventListener("contextmenu", context, true);
  }, [editor]);
  useEffect(() => {
    if (!menu) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setMenu(null);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    window.addEventListener("pointerdown", outside);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointerdown", outside);
      window.removeEventListener("keydown", key);
    };
  }, [menu]);
  const fit = (which: "board" | "sheet" | "selection") => {
    const bounds =
      which === "board"
        ? editor.getCurrentPageBounds()
        : which === "sheet" && active
          ? editor.getShapePageBounds(active)
          : editor.getSelectionPageBounds();
    if (bounds)
      editor.zoomToBounds(bounds, { inset: 240, animation: { duration: 200 } });
    setMenu(null);
  };
  const addShape = (type: "text" | "note") => {
    const page = point?.page;
    if (!page || !canEdit) return;
    if (type === "text") {
      addTextAt(editor, page);
      setMenu(null);
      return;
    }
    const { parentId, center } = imagePlacement(editor, page);
    const id = createShapeId();
    editor.markHistoryStoppingPoint("Add sticky note");
    editor.setCurrentTool("select");
    editor.createShape<TLNoteShape>({
      id,
      type,
      parentId,
      x: center.x - 100,
      y: center.y - 100,
      props: { color: "yellow" },
    });
    if (editor.getShape(id)) {
      editor.select(id);
      startEditingShapeWithRichText(editor, id);
      editor.focus();
    }
    setMenu(null);
  };

  const addComment = () => {
    const page = point?.page;
    if (!page || !canComment) return;
    const sheet = sheetAt(editor, page);
    controllers
      .get(editor)
      ?.comment(
        sheet ?? null,
        sheet ? editor.getPointInShapeSpace(sheet, page) : page,
      );
    setMenu(null);
  };
  const paste = async () => {
    const page = point?.page;
    if (!page || !canEdit) return;
    const action = pasteAtCanvasPoint.get(editor);
    setMenu(null);
    try {
      if (!action || !navigator.clipboard?.read)
        throw new Error(`Use ${pasteShortcut} to paste here.`);
      await action(page);
    } catch (error) {
      notify(
        error instanceof Error && error.name !== "NotAllowedError"
          ? error.message
          : `Clipboard access was denied. Use ${pasteShortcut} to paste.`,
      );
    }
  };
  return (
    <div ref={root} className="canvas-view-controls">
      <button
        className="fit-button"
        aria-label="Fit view"
        aria-expanded={menu === "fit"}
        onClick={() => {
          setPoint(null);
          setMenu(menu === "fit" ? null : "fit");
        }}
      >
        <Maximize size={17} /> Fit <ChevronDown size={12} />
      </button>
      <button
        aria-expanded={menu === "snap"}
        onClick={() => {
          setPoint(null);
          setMenu(menu === "snap" ? null : "snap");
        }}
      >
        Snap
      </button>
      {menu && (
        <div
          className={`canvas-view-menu floating-surface${menu === "canvas" ? " canvas-context-menu" : ""}`}
          role={menu === "canvas" ? "menu" : undefined}
          aria-label={menu === "canvas" ? "Canvas actions" : undefined}
          style={
            point
              ? {
                  position: "fixed",
                  left: point.x,
                  top: point.y,
                  bottom: "auto",
                }
              : undefined
          }
        >
          {menu === "canvas" ? (
            <>
              {canEdit && (
                <>
                  <button role="menuitem" onClick={() => void paste()}>
                    Paste
                    <span className="canvas-menu-shortcut">
                      {pasteShortcut}
                    </span>
                  </button>
                  <div className="canvas-menu-divider" role="separator" />
                  <button role="menuitem" onClick={() => addShape("text")}>
                    Add text
                  </button>
                  <button role="menuitem" onClick={() => addShape("note")}>
                    Add sticky note
                  </button>
                </>
              )}
              {canComment && (
                <button role="menuitem" onClick={addComment}>
                  Add comment
                </button>
              )}
              {(canEdit || canComment) && (
                <div className="canvas-menu-divider" role="separator" />
              )}
              <button
                role="menuitemcheckbox"
                aria-checked={view.grid}
                onClick={() => setView({ ...view, grid: !view.grid })}
              >
                <span className="canvas-menu-check">
                  {view.grid && <Check size={15} />}
                </span>
                Grid
              </button>
              <button
                role="menuitemcheckbox"
                aria-checked={objectSnap}
                onClick={() =>
                  editor.user.updateUserPreferences({
                    isSnapMode: !objectSnap,
                  })
                }
              >
                <span className="canvas-menu-check">
                  {objectSnap && <Check size={15} />}
                </span>
                Snap to objects
              </button>
              <div className="canvas-menu-divider" role="separator" />
              <button role="menuitem" onClick={() => fit("board")}>
                Show all
              </button>
              <button role="menuitem" onClick={() => setMenu("background")}>
                <Palette size={15} /> Canvas background
              </button>
              <button role="menuitem" onClick={() => setMenu("navigation")}>
                Mouse & trackpad
              </button>
            </>
          ) : menu === "fit" ? (
            <>
              <button
                onClick={() => {
                  setZoomPercent(editor, 100);
                  setMenu(null);
                }}
              >
                100%
              </button>
              <button onClick={() => fit("board")}>Fit board</button>
              <button disabled={!active} onClick={() => fit("sheet")}>
                Fit sheet
              </button>
              <button
                disabled={!model.selected.length}
                onClick={() => fit("selection")}
              >
                Fit selection
              </button>
              <button
                aria-pressed={model.tool === "hand"}
                onClick={() => {
                  model.choose("hand");
                  setMenu(null);
                }}
              >
                <Hand size={16} /> Pan
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={view.grid}
                  onChange={(event) =>
                    setView({ ...view, grid: event.target.checked })
                  }
                />
                Grid
              </label>
              <button onClick={() => setMenu("background")}>
                <Palette size={16} /> Canvas background
              </button>
              <button onClick={() => setMenu("navigation")}>
                Mouse & trackpad
              </button>
            </>
          ) : menu === "navigation" ? (
            <>
              <strong>Mouse & trackpad</strong>
              <label>
                <input
                  type="checkbox"
                  checked={wheelZoom}
                  onChange={(event) => {
                    setMouseWheelZoom(editor, event.target.checked);
                    setWheelZoom(event.target.checked);
                  }}
                />
                Scroll to zoom
              </label>
              <small>Pan: Space + drag or right-drag.</small>
              <small>Zoom: pinch or Ctrl/⌘ + scroll.</small>
            </>
          ) : menu === "snap" ? (
            <>
              <strong>Snap</strong>
              <label>
                <input
                  type="checkbox"
                  checked={view.grid}
                  onChange={(e) => setView({ ...view, grid: e.target.checked })}
                />
                Grid
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={objectSnap}
                  onChange={(e) =>
                    editor.user.updateUserPreferences({
                      isSnapMode: e.target.checked,
                    })
                  }
                />
                Objects
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={endpoints}
                  onChange={(e) => {
                    setEndpoints(e.target.checked);
                    setSnapEndpoints(editor, e.target.checked);
                  }}
                />
                Drawing endpoints
              </label>
              <small>Hold Alt to bypass endpoint snapping.</small>
            </>
          ) : (
            <>
              <strong>Canvas background</strong>
              <label>
                <input
                  type="radio"
                  name="canvas-grid"
                  checked={!view.grid}
                  onChange={() => setView({ ...view, grid: false })}
                />{" "}
                Plain
              </label>
              <label>
                <input
                  type="radio"
                  name="canvas-grid"
                  checked={view.grid && view.gridStyle === "dots"}
                  onChange={() =>
                    setView({ ...view, grid: true, gridStyle: "dots" })
                  }
                />{" "}
                Dots
              </label>
              <label>
                <input
                  type="radio"
                  name="canvas-grid"
                  checked={view.grid && view.gridStyle === "lines"}
                  onChange={() =>
                    setView({ ...view, grid: true, gridStyle: "lines" })
                  }
                />{" "}
                Lines
              </label>
              {view.grid && (
                <label>
                  Grid spacing{" "}
                  <input
                    aria-label="Grid spacing"
                    type="number"
                    min={4}
                    max={100}
                    value={view.spacing}
                    onChange={(e) => {
                      const spacing = Number(e.target.value);
                      if (spacing >= 4 && spacing <= 100)
                        setView({ ...view, spacing });
                    }}
                  />{" "}
                  units
                </label>
              )}
              <label>
                Canvas colour{" "}
                <input
                  type="color"
                  aria-label="Canvas background colour"
                  value={view.color}
                  onChange={(e) => setView({ ...view, color: e.target.value })}
                />
              </label>
              <small>Outside the sheets · only your view.</small>
              <button onClick={() => setMenu("navigation")}>
                Mouse & trackpad
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
